import { useState } from "react";
import { openDownloadUrl } from "util/openDownloadUrl";
import { useParams } from "react-router-dom";
import "static/styles/components/taskContractors.css";
import {
  useGetTaskMerchantsQuery,
  useLazyGetTaskMerchantsQuery,
} from "store/apis/taskMerchants";
import { AnalyseBankStatement } from "./AnalyseBankStatement";
import Loader from "./DesignSystem/Loader/loader";
import { NoContractor } from "./NoContractor";
import { IncompleteBooksOnboarding } from "./Task1099/IncompleteBooksOnboarding";
import { AddVendor } from "./Vendors/AddVendor/AddVendor";
import { MerchantTable } from "./Vendors/merchantTable";
import { Banner } from "./DesignSystem/Banner/Banner";
import { Button } from "./DesignSystem/Button/Button";
import { SmallArrowUpRight } from "components/icons/SmallArrowUpRight";
import { openLink } from "utils/openLink";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { ReviewTransactionsWithoutVendor } from "./ReviewTransactionsWithoutVendor/ReviewTransactionsWithoutVendor";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { Form1099VendorOptions } from "./Form1099Export/Form1099VendorOptions";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";

export const WSeriesForm = ({
  withFormProgress,
  groupId,
  noContractorsFound,
  taskSeason,
  isSuccessPage = false,
  shouldLoadBooksOnboarding,
  entityId,
}: {
  withFormProgress?: boolean;
  groupId: string;
  noContractorsFound: boolean;
  taskSeason: string;
  isSuccessPage?: boolean;
  shouldLoadBooksOnboarding: boolean;
  entityId: string;
}): React.JSX.Element | null => {
  const { taskId } = useParams<{ taskId: string }>();
  const { isCustomer, isAdmin } = useRoleBasedView();
  const [exportMerchants] = useLazyGetTaskMerchantsQuery();
  const { successToast, alertToast } = useToast();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();

  const [sorting, setSorting] = useState([
    {
      id: "AMOUNT",
      desc: false,
    },
  ]);

  const allQuery = {
    groupId,
    taskId,
    season: taskSeason,
    entityId,
  };

  const { data, isLoading: isContractorLoading } =
    useGetTaskMerchantsQuery(allQuery);
  const merchants = data?.merchants || [];

  const redirectToVendor = () => {
    if (isCustomer) {
      openLink(`/books/expenses/vendors/?entity=${entityId}`);
    } else {
      openLink(`/books/expenses/vendors?entity=${entityId}&company=${groupId}`);
    }
  };

  const exportVendors = async () => {
    try {
      const { document_id } = await exportMerchants({
        ...allQuery,
        generate_report: true,
      }).unwrap();
      const { download_url } = await getPreviewUrl({
        groupId,
        fileId: document_id,
      }).unwrap();
      openDownloadUrl(download_url);
      successToast({ message: "Vendor export successful" });
    } catch (e) {
      alertToast({ message: (e as BackendError).data?.error?.message });
    }
  };

  if (isContractorLoading) {
    return <Loader />;
  }

  if (shouldLoadBooksOnboarding) {
    return <IncompleteBooksOnboarding entityId={entityId} groupId={groupId} />;
  }

  if (noContractorsFound && merchants?.length === 0) {
    return <NoContractor />;
  }

  if (merchants?.length === 0) {
    return (
      <AnalyseBankStatement
        season={taskSeason}
        redirectToVendor={redirectToVendor}
      />
    );
  }

  const tableContent = (
    //@ts-ignore
    <MerchantTable
      merchants={merchants}
      groupId={groupId}
      sorting={sorting}
      setSorting={setSorting}
      entityId={entityId}
      selectedSeason={taskSeason}
    />
  );

  if (isSuccessPage) {
    return <div className="t-w-full">{tableContent}</div>;
  }

  return (
    <div className="task-contractors pb-3 overflow-auto t-mx-4">
      <div className="t-my-4">
        <div className="t-flex t-flex-col t-gap-4">
          <ReviewTransactionsWithoutVendor
            groupId={groupId}
            entityId={entityId}
            season={taskSeason ? Number(taskSeason) : undefined}
          />
          <Banner
            type="info"
            description="You can file Forms 1099 for up to three vendors. Additional filings
          are $20 each."
          />
        </div>
        <div className="t-flex t-justify-between t-items-center t-mb-3 t-mt-6 t-mr-2">
          <div className="t-text-subtitle-sm t-text-text-100">
            W Series and Form 1099
          </div>
          <div className="t-flex t-gap-3">
            <AddVendor entityId={entityId} />
            {isAdmin && redirectToVendor && exportVendors && (
              <Form1099VendorOptions
                exportVendors={exportVendors}
                redirectToVendor={redirectToVendor}
              />
            )}
          </div>
        </div>
        <div className="t-text-body-sm t-text-text-60">
          Below is the list of contractors from your bank statements. Please add
          their contact details and collect W-series forms. If a contractor is
          missing, inform us in chat.
        </div>
      </div>
      {tableContent}
    </div>
  );
};
